import { ApolloQueryResult, gql } from "@apollo/client";
import { EventPage } from "components/events";
import { FestivalPage } from "components/festival";
import { Layout } from "components/layout";
import { isValidMd5HashForEvent } from "lib/crypto";
import {
  BookCardDataFragmentDoc,
  EventCardDataFragmentDoc,
  GetEventConfLocationDocument,
  GetEventConfLocationQueryResult,
  GetEventConfLocationQueryVariables,
  GetEventDocument,
  GetEventQueryResult,
  GetEventQueryVariables,
  useGetEventQuery,
} from "lib/graphql-generated";
import { addApolloState, initializeApollo } from "lib/hooks/apollo-client";
import { useUser } from "lib/hooks/use-user";
import { parseParam } from "lib/utils";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import { useState } from "react";

const GET_EVENT = gql`
  query GetEvent($slug: String) {
    events(
      where: {
        _and: [{ slug: { _eq: $slug } }, { is_cancelled: { _eq: false } }]
      }
    ) {
      title
      slug
      id
      video
      start_time
      end_time
      location
      speakers
      seats
      price
      location
      is_festival
      is_online_only
      is_in_person_only
      is_book_club
      image
      free_seats
      enable_comments
      ## video_conf_location
      category_events {
        category {
          name
        }
      }
      event_books {
        book {
          ...BookCardData
        }
      }
      festival {
        ...EventCardData
        description
      }
      events(order_by: { start_time: asc }) {
        ...EventCardData
      }
      description
    }
  }
  ${EventCardDataFragmentDoc}
  ${BookCardDataFragmentDoc}
`;

const GET_CONF_LOCATION = gql`
  query GetEventConfLocation($id: uuid!) {
    events_by_pk(id: $id) {
      id
      video_conf_location
    }
  }
`;

interface Props {
  showConference: boolean;
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = initializeApollo();
  const accessParam = parseParam(ctx.query.video_conf_access);

  const variables: GetEventQueryVariables = {
    slug: parseParam(ctx.params.slug),
  };

  const { data }: ApolloQueryResult<GetEventQueryResult["data"]> =
    await client.query({
      query: GetEventDocument,
      variables: variables,
    });

  if (data.events.length > 0) {
    const {
      data: event_conf_data,
    }: ApolloQueryResult<GetEventConfLocationQueryResult["data"]> =
      await client.query({
        query: GetEventConfLocationDocument,
        variables: {
          id: data.events[0].id,
        } as GetEventConfLocationQueryVariables,
      });

    const shouldShowConference =
      isValidMd5HashForEvent(accessParam, data.events[0]) &&
      !!event_conf_data.events_by_pk.video_conf_location;

    return addApolloState(client, {
      props: {
        showConference: shouldShowConference,
      } as Props,
    });
  } else {
    return {
      notFound: true,
    };
  }
};

export const Event = ({ showConference }: Props) => {
  const router = useRouter();
  const { userData } = useUser();
  const [tab, setTab] = useState<"about" | "chat">("about");

  const { data } = useGetEventQuery({
    variables: {
      slug: parseParam(router.query.slug),
    },
    pollInterval: 3000,
  });

  const event = data.events[0];

  return (
    <>
      <Layout pageName={event.title} className='max-w-screen-2xl mx-auto'>
        {event.is_festival ? (
          <FestivalPage event={event} />
        ) : (
          <EventPage event={event} showConference={showConference} />
        )}
      </Layout>
    </>
  );
};

export default Event;
